import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@material-ui/core'
import green from '@material-ui/core/colors/green'
import CloseIcon from '@material-ui/icons/Close'
import { makeStyles } from '@material-ui/core/styles'
import { endOfDay, isValid, startOfDay, subMonths } from 'date-fns'
import memoizeOne from 'memoize-one'
import { InlineDatePicker } from 'material-ui-pickers'
import { MUIDataTableColumnDef } from 'mui-datatables'
import * as React from 'react'

import DataTable from '../../../components/DataTable'
import Fetcher from '../../../components/Fetcher'
import Flex from '../../../components/Flex'
import Margin from '../../../components/Margin'
import { t } from '../../../i18n'
import { format } from '../../../i18n/format'
import ImportHistory from '../../../models/ImportHistory'
import stockContributionService from '../../../services/stockContributionService'
import theme from '../../../theme'

const useStyles = makeStyles(() => ({
  content: {
    paddingTop: 0,
  },
}))

interface Props {
  open: boolean
  onClose: () => void
}

const statusLabels: { [key in ImportHistory['status']]: string } = {
  SUCCESS: t('Succès'),
  FAILED: t('Échec'),
  PENDING: t('En cours'),
}

const statusColors: { [key in ImportHistory['status']]: string } = {
  SUCCESS: green[700],
  FAILED: theme.palette.error.main,
  PENDING: theme.palette.grey[600],
}

const tableColumns: MUIDataTableColumnDef[] = [
  { name: 'id', options: { display: 'excluded', filter: false } },
  {
    name: 'status',
    label: t('Statut'),
    options: {
      customBodyRender: (value: string) => (
        <Typography
          style={{
            color: statusColors[value as ImportHistory['status']],
          }}
        >
          {statusLabels[value as ImportHistory['status']]}
        </Typography>
      ),
    },
  },
  t('Fichier'),
  { name: t('Date'), options: { filter: false } },
  { name: t('Lignes totales'), options: { filter: false } },
  { name: t('Lignes importées'), options: { filter: false } },
  { name: t('Dernier contrat'), options: { filter: false } },
]

const formatCreationDate = (creationDate?: Date | string | null) => {
  if (!creationDate) {
    return ' - '
  }
  const date = new Date(creationDate)
  return isValid(date) ? format(date, 'dd/MM/yyyy HH:mm') : ' - '
}

const filterHistory = (
  history: ImportHistory[],
  dateFrom?: Date | null,
  dateTo?: Date | null
) =>
  history.filter(importHistory => {
    const date = new Date(importHistory.creationDate).getTime()
    const from = dateFrom ? startOfDay(dateFrom).getTime() : null
    const to = dateTo ? endOfDay(dateTo).getTime() : null
    return (from === null || date >= from) && (to === null || date <= to)
  })

const historyToTableData = memoizeOne((history: ImportHistory[]) =>
  history.map(importHistory => [
    importHistory.id,
    importHistory.status,
    importHistory.fileName,
    formatCreationDate(importHistory.creationDate),
    importHistory.totalLines,
    importHistory.importedLines,
    importHistory.lastContract || ' - ',
  ])
)

export const ImportHistoryDialog = ({ open, onClose }: Props) => {
  const classes = useStyles()
  const [dateFrom, setDateFrom] = React.useState<Date | null>(() =>
    subMonths(new Date(), 1)
  )
  const [dateTo, setDateTo] = React.useState<Date | null>(null)

  const resetDateRange = () => {
    setDateFrom(null)
    setDateTo(null)
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle disableTypography>
        <Flex direction="row" alignItems="center">
          <Typography variant="h6">{t('Historique des imports')}</Typography>
          <Flex grow />
          <IconButton onClick={onClose} aria-label={t('Fermer')}>
            <CloseIcon />
          </IconButton>
        </Flex>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Margin bottom={1}>
          <Flex direction="row" alignItems="center">
            <InlineDatePicker
              label={t('Du')}
              value={dateFrom}
              onChange={setDateFrom}
              format="dd/MM/yyyy"
              mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
              style={{ flex: 1 }}
              keyboard
              autoOk
              variant="filled"
            />
            <Margin left={1} />
            <InlineDatePicker
              label={t('Au')}
              value={dateTo}
              onChange={setDateTo}
              format="dd/MM/yyyy"
              mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
              style={{ flex: 1 }}
              keyboard
              autoOk
              variant="filled"
            />
            <Margin left={1} />
            <Button
              color="primary"
              onClick={resetDateRange}
              disabled={!dateFrom && !dateTo}
            >
              {t('Réinitialiser')}
            </Button>
          </Flex>
        </Margin>
        <Fetcher fetch={stockContributionService.findHistory}>
          {history => (
            <DataTable
              columns={tableColumns}
              data={historyToTableData(
                filterHistory(history, dateFrom, dateTo)
              )}
              emptyContent={t('Aucun import effectué')}
            />
          )}
        </Fetcher>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          {t('Fermer')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ImportHistoryDialog
